// Pure domain logic for the Ordering / Orders / Reorder / Samples pages —
// behavior ported from admin-dashboard/public/index.html (line refs inline).
// Keep DOM-free: everything here is vitest-covered.

import { MAX_PRIZE_TIERS, mintedShopifyProducts, tiersFromSubmission } from './prize-tiers';

export type AnySub = Record<string, any>;
export type AnyOrder = Record<string, any>;
export type AnyInventoryItem = Record<string, any>;

export type ShopifyVariant = {
  id: number | string;
  title?: string;
  option1?: string;
  sku?: string;
  inventory_quantity?: number;
  inventoryQuantity?: number;
};

export const variantInventory = (v: ShopifyVariant): number =>
  v.inventory_quantity ?? v.inventoryQuantity ?? 0;

export const variantLabel = (v: ShopifyVariant): string => v.title || v.option1 || 'N/A';

function slotLogoPath(tierEntry: AnySub | undefined): string {
  const slots = Array.isArray(tierEntry?.decorationSlots) ? tierEntry.decorationSlots : [];
  const logo = slots.find((s: AnySub) => s && s.kind === 'logo') || slots[0];
  if (!logo) return '';
  // Prefer the trimmed slot art (t3s0 trimmed on N=3) over the raw upload.
  return String(logo.trimmedLogoPath || logo.logoPath || '');
}

// index.html:10189 getLatestLogoPath — newest revision wins; Product N reads
// tiers[n-1] (logoPath, then slot 0 trimmed/logo). T2 still follows T1 when
// useSameLogo. T3+ must NOT fall back to a leftover T1/T2-only field.
export function latestLogoPath(sub: AnySub, tier: number): string {
  const revisions: AnyOrder[] = Array.isArray(sub.revisions) ? sub.revisions : [];
  const tierKey = `tier${tier}`;
  for (let i = revisions.length - 1; i >= 0; i--) {
    const rev = revisions[i];
    if (rev && rev[tierKey] && rev[tierKey].path) return rev[tierKey].path;
  }
  const fromTiers = tiersFromSubmission(sub).find((t) => t.index === tier);
  const fromSlot = slotLogoPath(fromTiers);
  // Slot trimmed (t3s0) wins over a leftover untrimmed tiers[].logoPath.
  if (fromSlot) return fromSlot;
  if (fromTiers?.logoPath) return String(fromTiers.logoPath);
  if (tier === 1) {
    return sub.tier1LogoPath || sub.incomingPath || sub.finalPath || '';
  }
  if (fromTiers?.useSameLogo || (tier === 2 && sub.tier2UseSameLogo)) return latestLogoPath(sub, 1);
  return sub[`tier${tier}LogoPath`] || '';
}

// index.html:9306 — units from the LATEST subcollection order only (a reorder
// shouldn't show the lifetime total), falling back to the parent counter.
// N-tier: prefer totals.units[] (Product 1..N) over T1+T2 splits, which are 0
// on a 3-product order whose units live only on the array.
export function latestOrderUnits(sub: AnySub): number {
  const orders: AnyOrder[] = Array.isArray(sub.orders) ? sub.orders : [];
  const latest = orders.length > 0 ? orders[orders.length - 1] : null;
  if (latest) return orderDocUnits(latest);
  const fromParentUnits = Array.isArray(sub.totals?.units)
    ? sub.totals.units.reduce((s: number, n: unknown) => s + (Number(n) || 0), 0)
    : 0;
  if (fromParentUnits > 0) return fromParentUnits;
  const fromTiers = tiersFromSubmission(sub).reduce((s, t) => {
    const u = (t as AnySub).units;
    return s + (typeof u === 'number' ? u : 0);
  }, 0);
  if (fromTiers > 0) return fromTiers;
  return sub.totalUnitsOrdered || 0;
}

// Units for one orders-subcollection doc. Shapes seen live: create-order
// writes totals {tier1Units, tier2Units, totalUnits} (tier splits can be 0
// with only totalUnits real), orderwebhook writes tierNVariants /
// tierNQuantities, and both keep lineItems [{variant_id, quantity}].
export function orderDocUnits(o: AnyOrder): number {
  const fromUnitsArr = Array.isArray(o.totals?.units)
    ? o.totals.units.reduce((s: number, n: unknown) => s + (Number(n) || 0), 0)
    : 0;
  if (fromUnitsArr > 0) return fromUnitsArr;
  const fromTierTotals = (o.totals?.tier1Units || 0) + (o.totals?.tier2Units || 0);
  if (fromTierTotals > 0) return fromTierTotals;
  if (o.totals?.totalUnits > 0) return o.totals.totalUnits;
  if (typeof o.totalUnits === 'number' && o.totalUnits > 0) return o.totalUnits;
  const fromProductVariants = (o.productVariants || [])
    .flatMap((p: AnyOrder) => p.variants || [])
    .reduce((s: number, v: AnyOrder) => s + (v.quantity || 0), 0);
  if (fromProductVariants > 0) return fromProductVariants;
  const fromVariants = [...(o.tier1Variants || []), ...(o.tier2Variants || [])]
    .reduce((s: number, v: AnyOrder) => s + (v.quantity || 0), 0);
  if (fromVariants > 0) return fromVariants;
  const fromQuantities = [...Object.values(o.tier1Quantities || {}), ...Object.values(o.tier2Quantities || {})]
    .reduce((s: number, q) => s + ((q as number) || 0), 0);
  if (fromQuantities > 0) return fromQuantities;
  return (o.lineItems || o.line_items || [])
    .reduce((s: number, li: AnyOrder) => s + (li.quantity || 0), 0);
}

export type TrackingDisplay = {
  kind: 'delivered' | 'in-transit' | 'shipped' | 'unfulfilled';
  text: string;
  url?: string;
  number?: string;
  company?: string;
};

// index.html:9302 — an order placed after the last delivery means the
// tracking on file belongs to the PREVIOUS order; treat as unfulfilled.
export function hasStaleTracking(sub: AnySub): boolean {
  return Boolean(
    sub.lastOrderAt && sub.deliveredAt && new Date(sub.lastOrderAt) > new Date(sub.deliveredAt)
  );
}

// index.html:9358 getTrackingBadge.
export function trackingDisplay(sub: AnySub): TrackingDisplay {
  const source = hasStaleTracking(sub)
    ? { ...sub, fulfillmentStatus: 'unfulfilled', latestShipmentStatus: null, latestTrackingNumber: null, latestTrackingUrl: null, hasTracking: false }
    : sub;

  const status = source.fulfillmentStatus || 'unfulfilled';
  const shipmentStatus = source.latestShipmentStatus;
  const displayStatus = source.latestDisplayStatus;
  const number = source.latestTrackingNumber || undefined;
  const url = source.latestTrackingUrl || undefined;
  const company = source.latestTrackingCompany || undefined;

  if (status === 'delivered' || shipmentStatus === 'delivered') {
    return { kind: 'delivered', text: 'Delivered', url, number, company };
  }
  if (status === 'in_transit' || shipmentStatus === 'in_transit' || shipmentStatus === 'out_for_delivery') {
    return { kind: 'in-transit', text: displayStatus || 'In Transit', url, number, company };
  }
  if (status === 'fulfilled' || status === 'partial' || number) {
    return { kind: 'shipped', text: displayStatus || 'Shipped', url, number, company };
  }
  return { kind: 'unfulfilled', text: 'Unfulfilled' };
}

export type QtyRow = { size: string; quantity: number; sku?: string };

function shopifyNumericId(value: unknown): string {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const m = s.match(/\/(\d+)\s*$/);
  return m ? m[1] : s;
}

function qtyFromVariants(variants: AnyOrder[] | undefined): QtyRow[] | null {
  if (!variants || !variants.length || !variants.some((v) => (v.quantity || 0) > 0)) return null;
  return variants.filter((v) => (v.quantity || 0) > 0).map((v) => ({
    size: String(v.size || v.title || 'N/A'),
    quantity: Number(v.quantity) || 0,
    ...(v.sku ? { sku: String(v.sku) } : {}),
  }));
}

function qtyFromObject(obj: unknown): QtyRow[] | null {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const entries = Object.entries(obj as Record<string, unknown>).filter(([, q]) => Number(q) > 0);
  if (!entries.length) return null;
  return entries.map(([size, q]) => ({ size, quantity: Number(q) || 0 }));
}

function productVariantsFor(doc: AnyOrder | undefined, tier: number): AnyOrder[] | undefined {
  const list = doc?.productVariants;
  if (!Array.isArray(list)) return undefined;
  const hit = list.find((p: AnyOrder) => Number(p && p.index) === tier);
  return hit?.variants;
}

function designIdsForTier(sub: AnySub, tier: number): string[] {
  const entry = tiersFromSubmission(sub).find((t) => t.index === tier) as AnySub | undefined;
  const ids: string[] = [];
  if (entry?.preflightDesignId != null && String(entry.preflightDesignId)) ids.push(String(entry.preflightDesignId));
  for (const slot of Array.isArray(entry?.decorationSlots) ? entry.decorationSlots : []) {
    if (slot?.designId != null && String(slot.designId)) ids.push(String(slot.designId));
  }
  return ids;
}

function lineMatchesProduct(li: AnyOrder, shopifyProductId: string, designIds: string[]): boolean {
  const want = shopifyNumericId(shopifyProductId);
  const got = shopifyNumericId(li.product_id ?? li.productId);
  if (want && got && want === got) return true;
  const sku = String(li.sku || '');
  return designIds.some((id) => id && sku.includes(id));
}

function qtyFromLineItems(sub: AnySub, order: AnyOrder | null, tier: number): QtyRow[] | null {
  if (!order) return null;
  const minted = mintedShopifyProducts(sub).find((p) => p.index === tier);
  const designIds = designIdsForTier(sub, tier);
  const lines: AnyOrder[] = order.lineItems || order.line_items || [];
  const matched = lines.filter((li) => lineMatchesProduct(li, minted?.shopifyProductId || '', designIds));
  if (!matched.length || !matched.some((li) => (li.quantity || 0) > 0)) return null;
  return matched.filter((li) => (li.quantity || 0) > 0).map((li) => ({
    size: String(li.variant_title || li.variantTitle || li.size || li.title || 'N/A'),
    quantity: Number(li.quantity) || 0,
    ...(li.sku ? { sku: String(li.sku) } : {}),
  }));
}

// Product 1..N (cap 8) quantities. N-tier sources first: productVariants,
// quantities[n-1], line items. T1/T2 dual-read (tierNVariants / tierNQuantities)
// keeps old 2-product orders working. Never reads a `tier3Units` field.
export function tierQuantities(sub: AnySub, orders: AnyOrder[], tier: number): Array<[string, number]> {
  return productQuantityRows(sub, orders, tier).map((r) => [r.size, r.quantity]);
}

export function productQuantityRows(sub: AnySub, orders: AnyOrder[], tier: number): QtyRow[] {
  if (!Number.isInteger(tier) || tier < 1 || tier > MAX_PRIZE_TIERS) return [];
  const vKey = `tier${tier}Variants`;
  const qKey = `tier${tier}Quantities`;
  const latest = orders.length > 0 ? orders[orders.length - 1] : null;

  const parentPv = qtyFromVariants(productVariantsFor(sub, tier));
  if (parentPv) return parentPv;
  const parentLegacy = qtyFromVariants(sub[vKey]);
  if (parentLegacy) return parentLegacy;

  const latestPv = qtyFromVariants(productVariantsFor(latest || undefined, tier));
  if (latestPv) return latestPv;
  const latestLegacy = qtyFromVariants(latest?.[vKey]);
  if (latestLegacy) return latestLegacy;

  const nQty = qtyFromObject(latest?.quantities?.[tier - 1]);
  if (nQty) return nQty;
  const latestQuantities = qtyFromObject(latest?.[qKey]);
  if (latestQuantities) return latestQuantities;

  const fromLines = qtyFromLineItems(sub, latest, tier);
  if (fromLines) return fromLines;

  if (orders.length > 0) {
    const aggregated: Record<string, QtyRow> = {};
    for (const ord of orders) {
      const rows = qtyFromVariants(productVariantsFor(ord, tier))
        || qtyFromVariants(ord[vKey])
        || qtyFromObject(ord.quantities?.[tier - 1])
        || qtyFromObject(ord[qKey])
        || qtyFromLineItems(sub, ord, tier)
        || [];
      for (const r of rows) {
        const prev = aggregated[r.size];
        aggregated[r.size] = {
          size: r.size,
          quantity: (prev?.quantity || 0) + r.quantity,
          sku: r.sku || prev?.sku,
        };
      }
    }
    const out = Object.values(aggregated);
    if (out.length) return out;
  }

  return [];
}

// Product 1..N unit total. Prefer totals.units[n-1] / tiers[n-1].units
// (PR #298). Dual-read T1/T2 totals for old orders. Never invents tier3Units.
export function tierUnits(sub: AnySub, orders: AnyOrder[], tier: number): number {
  if (!Number.isInteger(tier) || tier < 1 || tier > MAX_PRIZE_TIERS) return 0;
  const latest = orders.length > 0 ? orders[orders.length - 1] : null;

  const fromUnitsArr = latest?.totals?.units?.[tier - 1];
  if (typeof fromUnitsArr === 'number' && fromUnitsArr > 0) return fromUnitsArr;

  const fromTiersEntry = tiersFromSubmission(sub)[tier - 1] as AnySub | undefined;
  const fromTierUnits = fromTiersEntry?.units;
  if (typeof fromTierUnits === 'number' && fromTierUnits > 0) return fromTierUnits;
  if (latest && typeof latest.tiers?.[tier - 1]?.units === 'number' && latest.tiers[tier - 1].units > 0) {
    return latest.tiers[tier - 1].units;
  }

  if (tier === 1 && sub.tier1TotalUnits) return sub.tier1TotalUnits;
  if (tier === 2 && sub.tier2TotalUnits) return sub.tier2TotalUnits;
  if (latest?.totals) {
    if (tier === 1 && latest.totals.tier1Units > 0) return latest.totals.tier1Units;
    if (tier === 2 && latest.totals.tier2Units > 0) return latest.totals.tier2Units;
  }

  const rows = productQuantityRows(sub, orders, tier);
  const sum = rows.reduce((s, r) => s + r.quantity, 0);
  if (sum > 0) return sum;
  return 0;
}

// index.html:9194 — orders before this date predate the tracking pipeline.
export const ORDERS_DATE_CUTOFF = new Date('2026-01-08T00:00:00.000Z');

export function afterCutoff(sub: AnySub): boolean {
  const orderDate = sub.firstOrderAt || sub.lastOrderAt || sub.createdAt;
  if (!orderDate) return false;
  return new Date(orderDate) >= ORDERS_DATE_CUTOFF;
}

export type ShippingFields = {
  firstName: string;
  lastName: string;
  company: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string; // 'US' | 'CA' code in the form
  phone: string;
};

export const EMPTY_SHIPPING: ShippingFields = {
  firstName: '', lastName: '', company: '', address1: '', address2: '',
  city: '', state: '', zip: '', country: 'US', phone: '',
};

// index.html:10175-10186 — prefill from shippingAddress/shipping with
// name-splitting and field-alias tolerance.
export function prefillShipping(sub: AnySub): ShippingFields {
  const shipTo = sub.shippingAddress || sub.shipping || {};
  return {
    firstName: shipTo.firstName || shipTo.name?.split(' ')[0] || '',
    lastName: shipTo.lastName || shipTo.name?.split(' ').slice(1).join(' ') || '',
    company: shipTo.company || '',
    address1: shipTo.address1 || shipTo.street || '',
    address2: shipTo.address2 || '',
    city: shipTo.city || '',
    state: shipTo.province || shipTo.state || '',
    zip: shipTo.zip || shipTo.postalCode || '',
    country: shipTo.country === 'Canada' || shipTo.country === 'CA' ? 'CA' : 'US',
    phone: shipTo.phone || sub.phone || '',
  };
}

export function shippingComplete(s: ShippingFields): boolean {
  return Boolean(s.firstName && s.lastName && s.address1 && s.city && s.state && s.zip);
}

const COUNTRY_NAME: Record<string, string> = { US: 'United States', CA: 'Canada' };

// Submit-time normalization, port of index.html:11000-11007 — trim every
// field and uppercase the state, so untouched prefilled values are cleaned
// the same way typed ones are.
function normalizeShipping(s: ShippingFields): ShippingFields {
  return {
    firstName: s.firstName.trim(),
    lastName: s.lastName.trim(),
    company: s.company.trim(),
    address1: s.address1.trim(),
    address2: s.address2.trim(),
    city: s.city.trim(),
    state: s.state.trim().toUpperCase(),
    zip: s.zip.trim(),
    country: s.country,
    phone: s.phone.trim(),
  };
}

export const isReorder = (sub: AnySub): boolean =>
  sub.hasOrders === true
  || sub.status === 'ordered'
  || Number(sub.orderCount) > 0
  || Boolean(sub.firstOrderAt || sub.lastOrderAt);

export const isReorderEligible = (sub: AnySub): boolean =>
  isReorder(sub) && mintedShopifyProducts(sub).length > 0;

export function reorderCandidates(submissions: AnySub[]): AnySub[] {
  const seen = new Set<string>();
  return submissions.filter((sub) => {
    if (!isReorderEligible(sub)) return false;
    const id = String(sub.id || '');
    if (id && seen.has(id)) return false;
    if (id) seen.add(id);
    return true;
  });
}

export type ReorderStatus = 'ordered' | 'final_approved';
export type ReorderSubmissionPage = {
  submissions: AnySub[];
  nextCursor: string | null;
};

export function reorderStatusUrl(status: ReorderStatus, cursor?: string): string {
  const cursorParam = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
  return `/api/submissions?limit=500&status=${status}${cursorParam}`;
}

function isReorderSubmissionPage(value: unknown): value is ReorderSubmissionPage {
  if (!value || typeof value !== 'object') return false;
  const page = value as Record<string, unknown>;
  if (!Array.isArray(page.submissions)) return false;
  if (!Object.prototype.hasOwnProperty.call(page, 'nextCursor')) return false;
  return page.nextCursor === null
    || (typeof page.nextCursor === 'string' && page.nextCursor.length > 0);
}

export async function fetchAllReorderSubmissions(
  status: ReorderStatus,
  fetchPage: (url: string) => Promise<unknown>,
): Promise<AnySub[]> {
  const submissions: AnySub[] = [];
  const followedCursors = new Set<string>();
  let cursor: string | undefined;

  while (true) {
    const page = await fetchPage(reorderStatusUrl(status, cursor));
    if (!isReorderSubmissionPage(page)) throw new Error('Invalid page returned while loading reorders');
    submissions.push(...page.submissions);
    if (page.nextCursor === null) return submissions;
    if (followedCursors.has(page.nextCursor)) {
      throw new Error('Repeated cursor returned while loading reorders');
    }
    followedCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  }
}

export type OrderLine = { variant_id: number | string; quantity: number; product_id?: number | string; sku?: string };

// index.html:11024-11066 — exact Shopify order payload + webhook metadata.
// allowDuplicate opts in to create-order's duplicate guard ONLY on reorders.
export function buildPlaceOrderPayload(args: {
  sub: AnySub;
  lines: OrderLine[];
  shipping: ShippingFields;
  email: string;
  financialStatus: 'paid' | 'pending';
  note: string;
  orderedBy: string;
}): { order: unknown; metadata: unknown } {
  const { sub, lines, email, financialStatus, note, orderedBy } = args;
  const shipping = normalizeShipping(args.shipping);
  return {
    order: {
      line_items: lines,
      customer: {
        first_name: shipping.firstName,
        last_name: shipping.lastName,
        email: email.trim() || `${sub.id}@ghostfitapparel.com`,
      },
      shipping_address: {
        first_name: shipping.firstName,
        last_name: shipping.lastName,
        company: shipping.company,
        address1: shipping.address1,
        address2: shipping.address2,
        city: shipping.city,
        province: shipping.state,
        country: COUNTRY_NAME[shipping.country] || shipping.country,
        zip: shipping.zip,
        phone: shipping.phone,
      },
      financial_status: financialStatus,
      note,
      tags: `fundraiser:${sub.id}, admin:${sub.adminName || 'Unknown'}`,
    },
    metadata: {
      submissionId: sub.id,
      fundraiserName: sub.fundraiserName,
      adminName: sub.adminName,
      tier1Product: sub.tier1Product,
      tier2Product: sub.tier2Product,
      tier1ShopifyProductId: sub.tier1ShopifyProductId,
      tier2ShopifyProductId: sub.tier2ShopifyProductId,
      products: mintedShopifyProducts(sub).map((p) => ({
        index: p.index,
        shopifyProductId: p.shopifyProductId,
      })),
      orderedAt: new Date().toISOString(),
      orderedBy,
      paymentStatus: financialStatus,
      allowDuplicate: isReorder(sub),
    },
  };
}

// create-order refuses any order past blank stock (owner ruling 2026-10-02 —
// no caller, the console included, can order past it, and there is no
// backorder escape): 409 `out_of_stock` / `stock_unverified`, or 503
// `stock_check_unavailable` when the check could not run. Nothing was created
// in any of these. Returns the operator-facing text, or null for every other
// failure (notably the duplicate-order 409, which keeps its own message).
const STOCK_REFUSAL_CODES = new Set(['out_of_stock', 'stock_unverified', 'stock_check_unavailable']);

export function stockRefusalMessage(status: number, body: unknown): string | null {
  if (status !== 409 && status !== 503) return null;
  if (!body || typeof body !== 'object') return null;
  const b = body as { code?: string; error?: string; detail?: string; hint?: string };
  if (!b.code || !STOCK_REFUSAL_CODES.has(b.code)) return null;
  return [b.error || 'Order refused: not enough blank stock', b.detail, b.hint].filter(Boolean).join('\n\n');
}

// The operator-facing text for a failed POST /api/orders/place. create-order
// answers 409 for three different reasons and only one of them is "this
// fundraiser was already ordered":
//   - stock refusal  — `code` out_of_stock / stock_unverified (stockRefusalMessage)
//   - approval gate  — `code: 'APPROVAL_INCOMPLETE'` (create-order
//     lib/approval-gate.js approvalIncompleteResponse, live 2026-10-21 #1310):
//     the submission is partial_approved / approving / pre-approval, or its
//     printer artwork is still waiting on a republish. Its error/detail/hint say
//     which, and what to do; showing "order already exists" here sent operators
//     to the Reorder page for a fundraiser that had no order at all.
//   - duplicate guard — no `code`, `existingOrderCount` (create-order index.js;
//     always set, `|| 1`). Only this one gets the Reorder-page message.
// Any other 409 (e.g. an in-flight orderAttemptId) shows its own error + hint.
export const DUPLICATE_ORDER_MESSAGE =
  'Order already exists for this fundraiser — use the Reorder page to place an additional order.';

export function placeOrderErrorMessage(status: number | null, body: unknown, fallback = 'Failed to create order'): string {
  if (status != null) {
    const stockMsg = stockRefusalMessage(status, body);
    if (stockMsg) return stockMsg;
  }
  const b = (body && typeof body === 'object' ? body : {}) as {
    code?: string; error?: string; message?: string; detail?: string; hint?: string; existingOrderCount?: unknown;
  };
  if (status === 409 && b.code === 'APPROVAL_INCOMPLETE') {
    return [
      b.error || 'Approval did not finish for this fundraiser, so it cannot be ordered. No order was created.',
      b.detail,
      b.hint,
    ].filter(Boolean).join('\n\n');
  }
  if (status === 409 && !b.code && typeof b.existingOrderCount === 'number') return DUPLICATE_ORDER_MESSAGE;
  if (status === 409) {
    return [b.message || b.error || fallback, b.detail, b.hint].filter(Boolean).join('\n\n');
  }
  return b.message || b.error || fallback;
}

// index.html:11567-11603 — sample/blank order payload + sampleOrders log row.
export function buildSampleOrderPayload(args: {
  item: AnyInventoryItem;
  lines: OrderLine[];
  shipping: ShippingFields;
  email: string;
  financialStatus: 'paid' | 'pending';
  note: string;
  orderedBy: string;
  totalUnits: number;
}): { orderPayload: unknown; sampleMetadata: unknown } {
  const { item, lines, email, financialStatus, note, orderedBy, totalUnits } = args;
  const shipping = normalizeShipping(args.shipping);
  const productName = [item.brand, item.color, item.category].filter(Boolean).join(' ');
  return {
    orderPayload: {
      order: {
        line_items: lines,
        customer: {
          first_name: shipping.firstName,
          last_name: shipping.lastName,
          email: email.trim() || 'samples@ghostfitapparel.com',
        },
        shipping_address: {
          first_name: shipping.firstName,
          last_name: shipping.lastName,
          company: shipping.company,
          address1: shipping.address1,
          address2: shipping.address2,
          city: shipping.city,
          province: shipping.state,
          country: COUNTRY_NAME[shipping.country] || shipping.country,
          zip: shipping.zip,
        },
        financial_status: financialStatus,
        note: `${note} [SAMPLE ORDER]`,
        tags: `sample-order, blank-product, ${item.styleNumber || ''}`,
      },
    },
    sampleMetadata: {
      masterInventoryId: item.id,
      shopifyProductId: item.shopifyProductId,
      productName,
      styleNumber: item.styleNumber,
      colorCode: item.colorCode,
      totalUnits,
      orderedBy,
      shipTo: {
        firstName: shipping.firstName,
        lastName: shipping.lastName,
        city: shipping.city,
        province: shipping.state,
      },
      paymentStatus: financialStatus,
    },
  };
}

// index.html:9412 getProductName — prize-product id → friendly name.
export function productNameOf(
  products: Array<{ id: string; shortName?: string; displayName?: string }>,
  id?: string
): string {
  if (!id) return '-';
  const p = products.find((x) => x.id === id);
  return p ? p.shortName || p.displayName || id : id;
}

// ---- Place Orders queue ordering --------------------------------------------
// The Place Orders queue is a WORK queue: a fundraiser enters it when the rep
// APPROVES the proof, not when the school first submitted it. Firestore serves
// /api/submissions ordered by `createdAt` because (status, createdAt) is the
// only composite index that exists for this collection — so a fundraiser that
// sat in the proof cycle for weeks and finally got approved renders wherever
// its ORIGINAL submission date puts it, not at the top.
//
// Real case (2026-08-25, id_9upmrcts "Pisckerington Central High School
// Soccer"): created 2026-06-23, approved 18:01 the same afternoon, rendered
// 450th of 454 rows. The rep reported it as "not appearing in my prize place
// orders tab". It was there — just 450 rows down an unpaginated table.
//
// Sorting here rather than in the Firestore query keeps the fix free of a new
// composite index (the /pipeline route in routes/proxy.js does the same thing
// for the same reason). `finalApprovedAt` is an ISO-8601 UTC string on every
// live final_approved doc, so a lexical compare IS a chronological compare and
// cannot produce the NaN that Date.parse() returns for a Firestore Timestamp.
export const approvalRecency = (sub: AnySub): string => String(sub.finalApprovedAt || '');

// Undated rows (no finalApprovedAt) sort to the TOP, not the bottom.
// Rationale: a row with no approval timestamp is an anomaly, and burying an
// anomaly at the bottom of a 450-row table is exactly the failure this function
// exists to fix. Surfacing it costs one glance; hiding it costs a fundraiser
// nobody sells. Flip the `? -1 : 1` below to bury them instead.
export function compareApprovalRecency(a: AnySub, b: AnySub): number {
  const av = approvalRecency(a);
  const bv = approvalRecency(b);
  if (!av || !bv) return av === bv ? 0 : (!av ? -1 : 1);
  return bv.localeCompare(av);
}

// Most-recently-approved first. Copies before sorting — the array handed in is
// SWR's cache value and must not be mutated in place.
export function orderingQueue(submissions: AnySub[]): AnySub[] {
  return [...submissions].sort(compareApprovalRecency);
}
